import { useState, useRef, useCallback } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, X } from 'lucide-react';

interface ColumnMapping {
  csvColumn: string;
  targetField: string;
}

const TARGET_FIELDS = [
  'Employee ID',
  'Name',
  'Email',
  'Department',
  'Title',
  'Location',
  'Start Date',
  'Status',
  'Manager',
  'Compensation',
  'Skip',
];

type UploadStep = 'upload' | 'mapping' | 'progress' | 'done';

export default function DataUploadPage() {
  const [step, setStep] = useState<UploadStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<string[][]>([]);
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parseCSV = (text: string) => {
    const lines = text.split('\n').filter((l) => l.trim());
    const headers = lines[0].split(',').map((h) => h.trim().replace(/"/g, ''));
    const rows = lines.slice(1, 6).map((line) =>
      line.split(',').map((cell) => cell.trim().replace(/"/g, ''))
    );
    return { headers, rows };
  };

  const handleFile = useCallback((f: File) => {
    if (!f.name.endsWith('.csv')) {
      alert('Please upload a CSV file.');
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const { headers, rows } = parseCSV(text);
      setCsvHeaders(headers);
      setPreviewRows(rows);
      setMappings(
        headers.map((h) => ({
          csvColumn: h,
          targetField: guessTargetField(h),
        }))
      );
      setStep('mapping');
    };
    reader.readAsText(f);
  }, []);

  const guessTargetField = (header: string): string => {
    const lower = header.toLowerCase();
    if (lower.includes('id')) return 'Employee ID';
    if (lower.includes('name')) return 'Name';
    if (lower.includes('email')) return 'Email';
    if (lower.includes('department') || lower.includes('dept')) return 'Department';
    if (lower.includes('title') || lower.includes('role')) return 'Title';
    if (lower.includes('location') || lower.includes('city')) return 'Location';
    if (lower.includes('start') || lower.includes('hire')) return 'Start Date';
    if (lower.includes('status')) return 'Status';
    if (lower.includes('manager')) return 'Manager';
    if (lower.includes('comp') || lower.includes('salary')) return 'Compensation';
    return 'Skip';
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer.files[0];
      if (f) handleFile(f);
    },
    [handleFile]
  );

  const handleUpload = () => {
    setStep('progress');
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setStep('done');
          return 100;
        }
        return prev + Math.random() * 15 + 5;
      });
    }, 300);
  };

  const updateMapping = (index: number, targetField: string) => {
    setMappings((prev) =>
      prev.map((m, i) => (i === index ? { ...m, targetField } : m))
    );
  };

  const reset = () => {
    setStep('upload');
    setFile(null);
    setCsvHeaders([]);
    setPreviewRows([]);
    setMappings([]);
    setProgress(0);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Data Upload</h1>
        <p className="mt-1 text-sm text-gray-500">
          Import employee data via CSV file
        </p>
      </div>

      {/* Step: Upload */}
      {step === 'upload' && (
        <div
          className={`card border-2 border-dashed transition-colors cursor-pointer ${
            dragOver
              ? 'border-primary-400 bg-primary-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="flex flex-col items-center justify-center py-12">
            <Upload className="h-10 w-10 text-gray-400 mb-4" />
            <p className="text-base font-medium text-gray-700">
              Drop your CSV file here, or click to browse
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Supports .csv files up to 50MB
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
          />
        </div>
      )}

      {/* Step: Column Mapping */}
      {step === 'mapping' && file && (
        <div className="space-y-4">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500">
                    {csvHeaders.length} columns, {previewRows.length}+ rows detected
                  </p>
                </div>
              </div>
              <button className="text-gray-400 hover:text-gray-500" onClick={reset}>
                <X className="h-5 w-5" />
              </button>
            </div>

            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Map your CSV columns to Knoetic fields
            </h3>

            <div className="space-y-2">
              {mappings.map((mapping, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-4 rounded-lg border border-gray-200 p-3"
                >
                  <span className="text-sm font-medium text-gray-700 w-40 truncate">
                    {mapping.csvColumn}
                  </span>
                  <span className="text-gray-400">-&gt;</span>
                  <select
                    value={mapping.targetField}
                    onChange={(e) => updateMapping(idx, e.target.value)}
                    className="input-field flex-1"
                  >
                    {TARGET_FIELDS.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Preview */}
          {previewRows.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Data Preview (first {previewRows.length} rows)
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead>
                    <tr>
                      {csvHeaders.map((h, i) => (
                        <th
                          key={i}
                          className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {previewRows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-3 py-2 text-gray-700">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button className="btn-secondary" onClick={reset}>
              Cancel
            </button>
            <button className="btn-primary" onClick={handleUpload}>
              Start Upload
            </button>
          </div>
        </div>
      )}

      {/* Step: Progress */}
      {step === 'progress' && (
        <div className="card">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            Uploading data...
          </h3>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
            <div
              className="bg-primary-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          <p className="text-sm text-gray-500">
            {Math.min(Math.round(progress), 100)}% complete
          </p>
        </div>
      )}

      {/* Step: Done */}
      {step === 'done' && (
        <div className="card text-center py-12">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Upload Complete
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            Your data has been imported successfully. It may take a few minutes
            for metrics to update.
          </p>
          <div className="flex justify-center gap-3">
            <button className="btn-secondary" onClick={reset}>
              Upload Another File
            </button>
            <a href="/dashboard" className="btn-primary">
              View Dashboard
            </a>
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="card bg-yellow-50 border-yellow-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-yellow-800">
              CSV Format Tips
            </h4>
            <ul className="mt-1 text-sm text-yellow-700 list-disc list-inside space-y-1">
              <li>First row must contain column headers</li>
              <li>Dates should be in YYYY-MM-DD format</li>
              <li>Use UTF-8 encoding for special characters</li>
              <li>Remove any empty rows at the end of the file</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
